import React, { useRef, useState } from 'react';

import { Modal as BootModal } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';

import { Button, FormTextField } from '../../components';

import './styles.scoped.css';

// CP-831 — manuals get their own cap (5 MB min per the ticket); the shared
// REACT_APP_PDF_MAX_SIZE (800 KB) stays untouched for DQF/receipt uploads.
const MAX_KB = Number(process.env.REACT_APP_MANUAL_PDF_MAX_SIZE);

const AddEditManualModal = ({ manual, onSave, onCancel, saving, saveError }) => {
  const isEdit = !!manual;
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState(manual?.title ?? '');
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const fileName = file?.name ?? manual?.originalFileName ?? '';

  const handleFileSelect = e => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.pdf')) {
      setErrors(prev => ({ ...prev, file: 'File type is not supported. Select a PDF.' }));
      return;
    }
    if (MAX_KB && selected.size / 1024 > MAX_KB) {
      setErrors(prev => ({ ...prev, file: `File size too large. Please size down to ${MAX_KB} KB.` }));
      return;
    }
    setErrors(prev => ({ ...prev, file: undefined }));
    setFile(selected);
  };

  const handleSave = () => {
    if (saving) return;
    const nextErrors = {};
    if (!title.trim()) nextErrors.title = 'Title is required.';
    if (!isEdit && !file) nextErrors.file = 'Add a PDF file.';
    // A rejected file selection leaves its error on screen — don't save past it
    if (errors.file) nextErrors.file = errors.file;

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    onSave({ title: title.trim(), file, fileSize: file ? file.size / 1024 : null });
  };

  return (
    <BootModal show onHide={onCancel}>
      <BootModal.Header>
        <BootModal.Title>Add / Edit Manual</BootModal.Title>
      </BootModal.Header>
      <BootModal.Body>
        <div className="modal-field">
          <FormTextField
            label="Title"
            required
            value={title}
            onChange={e => {
              setErrors(prev => ({ ...prev, title: undefined }));
              setTitle(e.target.value);
            }}
            error={!!errors.title}
            errorMessage={errors.title}
            disabled={saving}
          />
        </div>
        <div className="modal-field">
          <div className="file-grid">
            <span className="modal-label">Add File</span>
            <span className="modal-label">File Name</span>
            <Button
              variant="primary"
              className="add-file-btn"
              onPress={() => fileInputRef.current?.click()}
              disabled={saving}>
              <FaPlus />
            </Button>
            <span className="file-name">{fileName}</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden-input"
            onChange={handleFileSelect}
          />
          {errors.file && <span className="field-error">{errors.file}</span>}
        </div>
      </BootModal.Body>
      <BootModal.Footer>
        {saveError && <span className="field-error save-error">{saveError}</span>}
        <Button variant="primary" onPress={handleSave} text={saving ? 'Saving…' : 'Save'} disabled={saving} />
        <Button variant="outline" onPress={onCancel} text="Cancel" disabled={saving} />
      </BootModal.Footer>
    </BootModal>
  );
};

export default AddEditManualModal;
